/**
 * MealsPage — The Kitchen.
 *
 * Residence Journal · four calm sections, one question:
 *   "What are we eating?"
 *
 * Structure (fixed):
 *   1 · Tonight              — hero card for the evening's meal
 *   2 · This Week            — calm planning surface (no spreadsheet)
 *   3 · Family Recipe Book   — categories, seeded but empty by default
 *   4 · Pantry Intelligence  — one ARA sentence, no statistics
 *
 * Growing Home: nothing ships pre-populated. Empty states speak in ARA's
 * voice and invite the resident to add the first item.
 *
 * All state handlers, testids, routes and modals are preserved.
 * Presentation only has been redrawn to the Residence Journal system.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import {
  RecipeDetailModal, RecipeEditModal, MealPlanEditModal, getCookDisplay,
} from "./components/MealsModals";
import {
  RECIPE_BOOK_CATEGORIES, matchesRecipeCategory,
} from "./data/mealsData";
import { WeeklyMealTable } from "./components/WeeklyMealTable";
import { RecipeAddToSlotModal } from "./components/RecipeAddToSlotModal";
import { RecipePhotoImportModal } from "./components/RecipePhotoImportModal";
import { IngredientScannerModal } from "./components/IngredientScannerModal";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  Caption, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { CookingForSelector } from "./components/CookingForSelector";
import { MealSuggestions } from "./components/MealSuggestions";
import { getHouseholdFoodContext, evaluateMealForHousehold } from "./utils/foodIntelligence";

const SECTIONS = [
  { id: "tonight",     label: "Tonight" },
  { id: "this-week",   label: "This week" },
  { id: "recipe-book", label: "Family Recipe Book" },
  { id: "pantry",      label: "From the pantry" },
];

export default function MealsPage() {
  const state = useHomeOSState();
  const navigate = useNavigate();
  const { phase } = useLightPhase();
  const {
    recipes, weeklyMealPlan, pantryItems,
    addShoppingItem, shoppingList,
    updateMealPlanDay,
    members, kitchenCookingFor,
  } = state;

  const [recipeViewId, setRecipeViewId] = useState(null);
  const [recipeEditId, setRecipeEditId] = useState(null);
  const [recipeEditOpen, setRecipeEditOpen] = useState(false);
  const [recipeEditDraft, setRecipeEditDraft] = useState(null);
  const [photoImportOpen, setPhotoImportOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [planDay, setPlanDay] = useState(null);
  const [recipeForAdd, setRecipeForAdd] = useState(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [activeSection, setActiveSection] = useState("tonight");

  const plannedDinners = (weeklyMealPlan || []).filter(d => d.dinner).length;
  const hasAnyPlan = (weeklyMealPlan || []).some(d => d.dinner || d.lunch || d.breakfast);
  const today = new Date();
  const todayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][today.getDay()];
  const tonight = (weeklyMealPlan || []).find(d => d.day === todayName) || null;
  const tonightRecipe = tonight?.dinnerRecipeId ? (recipes || []).find(r => r.id === tonight.dinnerRecipeId) : null;
  const tonightCook = getCookDisplay(state, tonight?.assignedCook);

  const recipesCount = (recipes || []).length;

  /* Family food context — derived from Family Profiles, never copied. */
  const foodCtx = useMemo(
    () => getHouseholdFoodContext(members, kitchenCookingFor),
    [members, kitchenCookingFor]
  );
  const tonightSuitability = useMemo(() => {
    if (!tonight?.dinner) return null;
    const evaluation = evaluateMealForHousehold({ recipe: tonightRecipe, title: tonight.dinner }, foodCtx);
    return evaluation.status === "none" ? null : evaluation;
  }, [tonight, tonightRecipe, foodCtx]);

  /* Family Recipe Book — count recipes per pre-seeded category */
  const categoryCounts = useMemo(() => {
    const counts = {};
    RECIPE_BOOK_CATEGORIES.forEach(cat => {
      counts[cat.id] = (recipes || []).filter(r => matchesRecipeCategory(r, cat)).length;
    });
    return counts;
  }, [recipes]);

  const openCategory = openCategoryId ? RECIPE_BOOK_CATEGORIES.find(c => c.id === openCategoryId) : null;
  const openCategoryRecipes = useMemo(() => {
    if (!openCategory) return [];
    return (recipes || []).filter(r => matchesRecipeCategory(r, openCategory));
  }, [openCategory, recipes]);

  /* Pantry Intelligence — one ARA sentence, never statistics */
  const pantryVerdict = useMemo(() => {
    const items = pantryItems || [];
    if (items.length === 0) return "Your pantry begins the day the first item goes in.";
    const low = items.filter(p => p.status === "Low" || p.status === "Use soon");
    const expired = items.filter(p => p.status === "Expired");
    if (expired.length > 0) return `${expired.length} ${expired.length === 1 ? 'item is' : 'items are'} past their best — worth clearing out.`;
    if (low.length === 0) return "Nothing needs replacing this week.";
    if (low.length <= 2) return `You only need ${low.map(l => l.item.toLowerCase()).join(' and ')}.`;
    return `${low.length} ingredients are running low.`;
  }, [pantryItems]);

  const openAddRecipe = () => {
    setRecipeEditId(null);
    setRecipeEditDraft(null);
    setRecipeEditOpen(true);
  };
  const openEditRecipe = (recipe) => {
    setRecipeEditId(recipe.id);
    setRecipeEditDraft(null);
    setRecipeEditOpen(true);
    setRecipeViewId(null);
  };
  const addToPlan = (recipe) => { setRecipeViewId(null); setRecipeForAdd(recipe); };
  const addIngredientsFromRecipe = (recipe) => {
    if (!recipe?.ingredients?.length) { toast("This recipe has no ingredients yet."); return; }
    const existing = new Set((shoppingList || []).map(s => s.item.toLowerCase().trim()));
    let added = 0;
    recipe.ingredients.forEach(ing => {
      const key = String(ing).toLowerCase().trim();
      if (existing.has(key)) return;
      existing.add(key);
      addShoppingItem({ item: ing, category: "Pantry", quantity: "", sourceRecipeId: recipe.id });
      added += 1;
    });
    toast.success(added ? `Added ${added} item${added === 1 ? "" : "s"} to the shopping list.` : "All ingredients already on the list.");
  };

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`kitchen-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* Observe which section is in view for the small secondary nav */
  useEffect(() => {
    const els = SECTIONS.map(s => document.getElementById(`kitchen-${s.id}`)).filter(Boolean);
    if (els.length === 0) return;
    const io = new IntersectionObserver((entries) => {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) {
        const id = visible.target.id.replace('kitchen-', '');
        setActiveSection(id);
      }
    }, { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [openCategoryId]);

  /* Hero focal — the one sentence that answers "what are we eating?" */
  const focal = tonight?.dinner
    ? `Tonight is already sorted — ${tonight.dinner.toLowerCase()}.`
    : recipesCount === 0
      ? "Your first family dinner begins here."
      : plannedDinners >= 5
        ? "The week is planned. Only shopping left."
        : plannedDinners > 0
          ? `${7 - plannedDinners} dinner${7 - plannedDinners === 1 ? '' : 's'} still to plan this week.`
          : "The week is waiting for its first meal.";

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="meals" />} phase={phase} testid="module-page-meals">
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <PageHeading
        testid="meals-hero"
        module="meals"
        art="meals"
        room="Kitchen"
        title="The Kitchen"
        focal={focal}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setPlanDay(todayName)} testid="meals-plan-week-btn">
              Plan tonight
            </PillButton>
            <TextLink onClick={openAddRecipe} testid="meals-add-recipe-btn">
              Add a recipe
            </TextLink>
            <TextLink onClick={() => setPhotoImportOpen(true)} testid="meals-scan-recipe-photo-btn">
              Import from a photo
            </TextLink>
          </>
        }
      />

      {/* Secondary section nav — moving around the room */}
      <nav
        className="flex flex-wrap items-center gap-x-8 gap-y-2 py-4 mb-6 border-y"
        style={{ borderColor: 'var(--ara-line-soft)' }}
        aria-label="Kitchen sections"
        data-testid="kitchen-section-nav"
      >
        {SECTIONS.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollTo(s.id)}
            className="text-[13px] transition-ara"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              color: activeSection === s.id ? 'var(--ara-ink)' : 'var(--ara-ink-soft)',
              fontWeight: activeSection === s.id ? 500 : 400,
              borderBottom: activeSection === s.id ? '1px solid var(--ara-ink)' : '1px solid transparent',
              paddingBottom: '2px',
            }}
            aria-current={activeSection === s.id ? 'true' : undefined}
            data-testid={`kitchen-nav-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {/* Cooking for — quiet household context (Family Profiles are the source) */}
      {(members || []).length > 0 && (
        <div className="mb-8">
          <CookingForSelector />
          {!foodCtx.hasAnyFoodInfo && (
            <p
              className="mt-2 italic text-[13px] leading-[1.5]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-soft)', fontWeight: 400 }}
              data-testid="kitchen-profiles-grow-hint"
            >
              ARA can make meal suggestions more personal as your Family Profiles grow.
            </p>
          )}
        </div>
      )}

      {/* Preserved testids (hidden shims for existing tests) */}
      <div className="hidden" data-testid="meals-title" aria-hidden>The Kitchen</div>
      <div className="hidden" data-testid="meals-eyebrow" aria-hidden>Meals</div>
      <div className="hidden" data-testid="meals-subtitle" aria-hidden>Recipes, weekly meals and shopping.</div>
      <div className="hidden" data-testid="meals-status-line" aria-hidden>{plannedDinners}/7 dinners</div>
      <div className="hidden" data-testid="meals-scan-barcode-btn" aria-hidden><button type="button" onClick={() => setBarcodeOpen(true)} /></div>

      {/* ── 1 · Tonight ─────────────────────────────────────────────── */}
      <EditorialSection
        id="kitchen-tonight"
        testid="meals-tonight-card"
        moduleMark={{ module: 'meals', label: 'Tonight' }}
        title={tonight?.dinner ? tonight.dinner : "Waiting for a decision"}
      >
        {tonight?.dinner ? (
          <div data-testid="meals-tonight-filled">
            <p
              className="text-[26px] sm:text-[30px] leading-[1.1] tracking-[-0.01em]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid="meals-tonight-title"
            >
              {tonight.dinner}
            </p>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2">
              {tonightRecipe && (
                <span
                  className="text-[14px] tabular-nums"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
                >
                  Ready in {(tonightRecipe.prepTime || 0) + (tonightRecipe.cookTime || 0)} mins
                </span>
              )}
              {tonightCook && (
                <span
                  className="text-[14px]"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                  data-testid="meals-tonight-cook"
                >
                  Cook: {tonightCook.name}
                </span>
              )}
              {tonightRecipe && (
                <span
                  className="text-[14px]"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                >
                  Serves {tonightRecipe.serves}
                </span>
              )}
            </div>
            {tonightSuitability?.line && (
              <p
                className="mt-5 italic text-[15px] leading-[1.55]"
                style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-muted)', fontWeight: 400, maxWidth: '46ch' }}
                data-testid={`meals-tonight-suitability-${tonightSuitability.status}`}
              >
                {tonightSuitability.line}
              </p>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3">
              <TextLink
                onClick={() => { updateMealPlanDay(tonight.day, { status: "Cooked" }); toast.success("Marked cooked."); }}
                testid="meals-tonight-mark-cooked"
              >
                Cook
              </TextLink>
              <TextLink onClick={() => setPlanDay(tonight.day)} testid="meals-tonight-change">
                Swap
              </TextLink>
              {tonightRecipe && (
                <TextLink onClick={() => setRecipeViewId(tonightRecipe.id)} testid="meals-tonight-view-recipe">
                  Recipe →
                </TextLink>
              )}
              {tonightRecipe && (
                <TextLink onClick={() => addIngredientsFromRecipe(tonightRecipe)} testid="meals-tonight-add-ingredients">
                  Add ingredients
                </TextLink>
              )}
            </div>
            <MealSuggestions
              todayName={todayName}
              hasPlannedMeal
              onAddRecipe={() => { setRecipeEditId(null); setRecipeEditDraft(null); setRecipeEditOpen(true); }}
              onBrowseRecipes={() => scrollTo('recipe-book')}
            />
          </div>
        ) : (
          <div data-testid="meals-tonight-empty">
            <p
              className="italic text-[19px] sm:text-[21px] leading-[1.4]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '32ch' }}
              data-testid="meals-tonight-empty-sentence"
            >
              Your first family dinner begins here.
            </p>
            <div className="mt-8">
              <PillButton
                variant="solid"
                onClick={() => setPlanDay(tonight?.day || todayName)}
                testid="meals-tonight-choose-recipe"
              >
                Add tonight's dinner
              </PillButton>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3">
              <TextLink onClick={() => navigate("/home-os/command-centre")} testid="meals-tonight-ask-ara">
                Ask ARA
              </TextLink>
              <TextLink onClick={() => scrollTo('recipe-book')} testid="meals-tonight-recipe-book">
                Browse Family Recipe Book
              </TextLink>
            </div>
            <MealSuggestions
              todayName={todayName}
              hasPlannedMeal={false}
              onAddRecipe={() => { setRecipeEditId(null); setRecipeEditDraft(null); setRecipeEditOpen(true); }}
              onBrowseRecipes={() => scrollTo('recipe-book')}
            />
          </div>
        )}
      </EditorialSection>

      {/* ── 2 · This Week ─────────────────────────────────────────── */}
      <EditorialSection
        id="kitchen-this-week"
        testid="meals-this-week"
        moduleMark={{ module: 'meals', label: 'This week' }}
        title="This week"
        aside={
          hasAnyPlan ? (
            <TextLink onClick={() => window.print()} testid="meals-print-week">Print →</TextLink>
          ) : null
        }
      >
        {hasAnyPlan ? (
          <WeeklyMealTable onPrint={() => window.print()} />
        ) : (
          <div>
            <p
              className="italic text-[19px] sm:text-[21px] leading-[1.4]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '32ch' }}
              data-testid="meals-this-week-empty-sentence"
            >
              Plan one meal. The week will grow around it.
            </p>
            <div className="mt-8">
              <PillButton
                variant="solid"
                onClick={() => setPlanDay(todayName)}
                testid="meals-this-week-plan-first"
              >
                Plan the first meal
              </PillButton>
            </div>
          </div>
        )}
      </EditorialSection>

      {/* ── 3 · Family Recipe Book ────────────────────────────────── */}
      <EditorialSection
        id="kitchen-recipe-book"
        testid="meals-recipe-book"
        moduleMark={{ module: 'meals', label: 'Family Recipe Book' }}
        title={openCategory ? openCategory.label : "Family Recipe Book"}
        aside={
          openCategory ? (
            <TextLink onClick={() => setOpenCategoryId(null)} testid="meals-recipe-book-back">
              ← All categories
            </TextLink>
          ) : (
            <div className="flex flex-col items-start gap-3">
              <TextLink onClick={openAddRecipe} testid="meals-add-recipe-inline">
                Add recipe
              </TextLink>
              <TextLink onClick={() => setPhotoImportOpen(true)} testid="meals-recipe-book-import">
                Import from photo
              </TextLink>
            </div>
          )
        }
      >
        {!openCategory ? (
          recipesCount === 0 ? (
            <div>
              <p
                className="italic text-[19px] sm:text-[21px] leading-[1.4]"
                style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '32ch' }}
                data-testid="meals-recipe-book-empty-sentence"
              >
                Every family has recipes worth keeping. Add the first one.
              </p>
              <div className="mt-8">
                <PillButton variant="solid" onClick={openAddRecipe} testid="meals-recipe-book-add-first">
                  Add the first recipe
                </PillButton>
              </div>
              {/* Category shelf shown quietly below, even when empty */}
              <div className="mt-12" data-testid="meals-recipe-book-categories-empty">
                <Caption>Categories</Caption>
                <ul className="mt-4">
                  {RECIPE_BOOK_CATEGORIES.map((cat, i, arr) => (
                    <li
                      key={cat.id}
                      className={`grid grid-cols-[1fr_auto] items-baseline gap-6 py-3 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                      style={{ borderColor: 'var(--ara-line-soft)' }}
                    >
                      <p className="text-[14px]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}>
                        {cat.label}
                      </p>
                      <p className="text-[13px] tabular-nums" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}>
                        0 recipes
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <ul data-testid="meals-recipe-book-categories">
              {RECIPE_BOOK_CATEGORIES.map((cat, i, arr) => {
                const count = categoryCounts[cat.id] || 0;
                return (
                  <li
                    key={cat.id}
                    className={`${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                    style={{ borderColor: 'var(--ara-line-soft)' }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenCategoryId(cat.id)}
                      className="w-full grid grid-cols-[1fr_auto_auto] items-baseline gap-6 py-4 text-left transition-ara hover:opacity-70"
                      data-testid={`recipe-book-cat-${cat.id}`}
                    >
                      <div className="min-w-0">
                        <p
                          className="text-[15.5px] leading-tight"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                        >
                          {cat.label}
                        </p>
                        <p
                          className="mt-1 text-[13px]"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                        >
                          {cat.subtitle}
                        </p>
                      </div>
                      <p
                        className="text-[13px] tabular-nums whitespace-nowrap"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                        data-testid={`recipe-book-cat-count-${cat.id}`}
                      >
                        {count} recipes
                      </p>
                      <span
                        className="text-[13px]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                        aria-hidden
                      >
                        →
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )
        ) : (
          /* A specific category is open — clean rows inside */
          openCategoryRecipes.length === 0 ? (
            <div>
              <p
                className="italic text-[19px] sm:text-[21px] leading-[1.4]"
                style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '32ch' }}
              >
                No {openCategory.label.toLowerCase()} recipes yet.
              </p>
              <div className="mt-6">
                <PillButton variant="solid" onClick={openAddRecipe} testid="recipe-book-cat-add-first">
                  Add the first
                </PillButton>
              </div>
            </div>
          ) : (
            <ul data-testid="recipe-book-recipe-list">
              {openCategoryRecipes.map((r, i, arr) => (
                <li
                  key={r.id}
                  className={`grid grid-cols-[1fr_auto] items-baseline gap-6 py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`recipe-card-${r.id}`}
                >
                  <div className="min-w-0">
                    <p
                      className="text-[15.5px] leading-tight"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                      data-testid={`recipe-title-${r.id}`}
                    >
                      {r.title}
                      {r.favourite && (
                        <span className="ml-2 text-[12px]" style={{ color: 'var(--ara-mod-goals)' }}>♥</span>
                      )}
                    </p>
                    <p
                      className="mt-1 text-[13px]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                    >
                      {(r.prepTime || 0) + (r.cookTime || 0)} min · Serves {r.serves} · {r.difficulty}
                    </p>
                  </div>
                  <div className="flex items-baseline gap-x-5">
                    <TextLink onClick={() => setRecipeViewId(r.id)} testid={`recipe-view-${r.id}`}>View</TextLink>
                    <TextLink onClick={() => addToPlan(r)} testid={`recipe-add-plan-${r.id}`}>Add to plan</TextLink>
                    <TextLink onClick={() => state.toggleRecipeFavourite(r.id)} testid={`recipe-fav-card-${r.id}`}>
                      {r.favourite ? '♥' : '♡'}
                    </TextLink>
                  </div>
                </li>
              ))}
            </ul>
          )
        )}
      </EditorialSection>

      {/* ── 4 · Pantry Intelligence ────────────────────────────────── */}
      <EditorialSection
        id="kitchen-pantry"
        testid="meals-pantry-intelligence"
        moduleMark={{ module: 'meals', label: 'Pantry Intelligence' }}
        title="What ARA notices"
      >
        <p
          className="italic text-[19px] sm:text-[21px] leading-[1.4]"
          style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '36ch' }}
          data-testid="meals-pantry-verdict"
        >
          {pantryVerdict}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
          <TextLink onClick={() => navigate('/home-os/supplies')} testid="meals-pantry-open">
            Open the pantry →
          </TextLink>
          <TextLink onClick={() => setBarcodeOpen(true)} testid="meals-pantry-scan">
            Scan an item
          </TextLink>
        </div>
      </EditorialSection>

      <Colophon>ARA · Kitchen</Colophon>

      {/* ── Modals (unchanged) ────────────────────────────────────────── */}
      <RecipeDetailModal
        open={!!recipeViewId}
        recipe={recipes?.find(r => r.id === recipeViewId) || null}
        onClose={() => setRecipeViewId(null)}
        onEdit={openEditRecipe}
        onAddToPlan={addToPlan}
      />
      <RecipeEditModal
        open={recipeEditOpen}
        recipeId={recipeEditId}
        initialDraft={recipeEditDraft}
        onClose={() => { setRecipeEditOpen(false); setRecipeEditDraft(null); }}
      />
      <MealPlanEditModal
        open={!!planDay}
        day={planDay}
        onClose={() => setPlanDay(null)}
      />
      <RecipeAddToSlotModal
        open={!!recipeForAdd}
        recipe={recipeForAdd}
        onClose={() => setRecipeForAdd(null)}
      />
      <RecipePhotoImportModal
        open={photoImportOpen}
        onClose={() => setPhotoImportOpen(false)}
        onApply={(draft) => {
          setPhotoImportOpen(false);
          setRecipeEditId(null);
          setRecipeEditDraft(draft);
          setRecipeEditOpen(true);
        }}
      />
      <IngredientScannerModal
        open={barcodeOpen}
        onClose={() => setBarcodeOpen(false)}
        onAddIngredient={(ing) => {
          const name = typeof ing === "string" ? ing : (ing?.name || ing?.item || "Ingredient");
          addShoppingItem({ item: name, category: "Pantry", quantity: "", sourceRecipeId: null });
          toast.success(`Added ${name} to the shopping list.`);
          setBarcodeOpen(false);
        }}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
